import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Instagram, Youtube, Linkedin, Facebook, ArrowUpRight, MapPin, Phone, Mail, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import FocusPull from "@/components/site/FocusPull";

const SOCIALS = [
  { icon: Instagram, label: "Instagram", testid: "social-instagram" },
  { icon: Youtube, label: "YouTube", testid: "social-youtube" },
  { icon: Linkedin, label: "LinkedIn", testid: "social-linkedin" },
  { icon: Facebook, label: "Facebook", testid: "social-facebook" },
];

export const CONTACT = {
  address: "604, Mont Vert Velocity, Baner-Pashan Link Road, Pune, Maharashtra",
  phone: "+91 80553 16262",
  email: "academy@lifeinsidemedia.com",
  hours: "9:00 AM — 6:00 PM",
  mapsQuery: "Mont Vert Velocity, Baner-Pashan Link Road, Pune, Maharashtra",
};

const inputCls =
  "w-full bg-transparent border-b border-white/20 focus:border-lima-blue outline-none text-lima-paper text-sm px-0 py-3 placeholder:text-white/30 transition-colors duration-200 rounded-none";

export default function ContactSection({ courses, initialProgram }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", program: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (initialProgram) {
      const match = courses.find((c) => c.slug === initialProgram);
      if (match) setForm((f) => ({ ...f, program: match.title }));
    }
  }, [initialProgram, courses]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Message received — we'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", program: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-lima-ink border-t border-white/10" data-testid="contact-section">
      <div className="px-5 md:px-10 pt-14 md:pt-32 pb-12 md:pb-24 grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <FocusPull amount={16} y={30}>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-lima-paper leading-[1.0]">
              Roll on
              <br />
              <span className="font-serifit font-normal text-lima-blue">your story.</span>
            </h2>
          </FocusPull>
          <p className="mt-6 max-w-md text-sm md:text-base text-white/55 leading-relaxed">
            No prior experience needed. Whether you're starting out or sharpening existing skills — tell us where you
            want to go and we'll map the route.
          </p>

          <div className="mt-12 space-y-8">
            <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="group block" data-testid="contact-phone-link">
              <p className="font-mono2 text-[11px] tracking-[0.25em] text-white/40 mb-1.5 flex items-center gap-2">
                <Phone size={13} className="text-lima-blue" /> CALL US
              </p>
              <p className="font-display font-bold text-2xl md:text-3xl tracking-tight text-lima-paper group-hover:text-lima-blue transition-colors duration-200">
                {CONTACT.phone}
              </p>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="group block" data-testid="contact-email-link">
              <p className="font-mono2 text-[11px] tracking-[0.25em] text-white/40 mb-1.5 flex items-center gap-2">
                <Mail size={13} className="text-lima-blue" /> WRITE TO US
              </p>
              <p className="font-display font-bold text-2xl md:text-3xl tracking-tight text-lima-paper group-hover:text-lima-blue transition-colors duration-200 break-all">
                {CONTACT.email}
              </p>
            </a>
            <div data-testid="contact-row-address">
              <p className="font-mono2 text-[11px] tracking-[0.25em] text-white/40 mb-1.5 flex items-center gap-2">
                <MapPin size={13} className="text-lima-blue" /> VISIT US
              </p>
              <p className="font-display font-bold text-xl md:text-2xl tracking-tight text-lima-paper leading-snug">
                {CONTACT.address}
              </p>
            </div>
            <div data-testid="contact-row-hours">
              <p className="font-mono2 text-[11px] tracking-[0.25em] text-white/40 mb-1.5 flex items-center gap-2">
                <Clock size={13} className="text-lima-blue" /> STUDIO HOURS
              </p>
              <p className="font-display font-bold text-xl md:text-2xl tracking-tight text-lima-paper">{CONTACT.hours}</p>
            </div>
          </div>

          <div className="mt-10 flex gap-3">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href="#contact"
                aria-label={s.label}
                data-testid={s.testid}
                className="w-10 h-10 grid place-items-center border border-white/15 text-white/60 hover:text-white hover:border-lima-blue transition-colors duration-200"
              >
                <s.icon size={16} />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-7">
          <FocusPull delay={0.12} amount={12} className="md:pt-24">
            <form onSubmit={submit} data-testid="enquiry-form">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                <div>
                  <label className="block font-mono2 text-[11px] tracking-[0.25em] text-white/40 mb-1">NAME *</label>
                  <input required data-testid="contact-name-input" className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} />
                </div>
                <div>
                  <label className="block font-mono2 text-[11px] tracking-[0.25em] text-white/40 mb-1">EMAIL *</label>
                  <input required type="email" data-testid="contact-email-input" className={inputCls} placeholder="you@email.com" value={form.email} onChange={set("email")} />
                </div>
                <div>
                  <label className="block font-mono2 text-[11px] tracking-[0.25em] text-white/40 mb-1">PHONE</label>
                  <input data-testid="contact-phone-input" className={inputCls} placeholder="+91 …" value={form.phone} onChange={set("phone")} />
                </div>
                <div>
                  <label className="block font-mono2 text-[11px] tracking-[0.25em] text-white/40 mb-1">PROGRAM OF INTEREST</label>
                  <select data-testid="contact-program-select" className={`${inputCls} bg-lima-ink`} value={form.program} onChange={set("program")}>
                    <option value="">Not sure yet</option>
                    {courses.map((c) => (
                      <option key={c.id} value={c.title}>{c.title}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-7">
                <label className="block font-mono2 text-[11px] tracking-[0.25em] text-white/40 mb-1">MESSAGE</label>
                <textarea rows={5} data-testid="contact-message-input" className={`${inputCls} resize-none`} placeholder="Tell us about yourself…" value={form.message} onChange={set("message")} />
              </div>
              <motion.button
                whileTap={{ scale: 0.96 }}
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="mt-10 bg-lima-blue hover:bg-lima-bluehover disabled:opacity-50 text-white font-mono2 text-xs font-bold tracking-[0.2em] px-8 py-4 transition-colors duration-200 flex items-center gap-3"
              >
                {sending ? "SENDING…" : "SEND MESSAGE"} <ArrowUpRight size={15} />
              </motion.button>
            </form>
          </FocusPull>
        </div>
      </div>

      {/* Full-width map strip */}
      <div className="relative border-t border-white/10" data-testid="contact-map">
        <iframe
          title="LIMA — Lime Studios location map"
          src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapsQuery)}&output=embed`}
          className="w-full h-72 md:h-96 grayscale contrast-125 opacity-80"
          loading="lazy"
        />
        <div className="absolute bottom-6 left-5 md:left-10 bg-lima-blue px-6 py-5 max-w-sm" data-testid="map-address-card">
          <p className="font-mono2 text-[10px] tracking-[0.25em] text-white/70 mb-1.5">LIME STUDIOS — LIMA CAMPUS</p>
          <p className="font-display font-bold text-lg md:text-xl text-white leading-snug">{CONTACT.address}</p>
        </div>
      </div>
    </section>
  );
}
